import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#selectMainFeaturedMediaOnInitialLoad();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        newMediaGallery.dataset.variantSelected = 'true';
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps the product's main featured media visible on an initial product-page load.
   * Variant selections continue to use the server-rendered gallery returned by Horizon.
   */
  #selectMainFeaturedMediaOnInitialLoad() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.#selectMainFeaturedMediaOnMobileLoad();
      return;
    }

    if (this.dataset.variantSelected === 'true') return;
    if (!this.closest("main[data-template^='product']")) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    if (new URLSearchParams(window.location.search).has('variant')) return;

    const mainFeaturedMediaId = this.dataset.mainFeaturedMediaId;
    if (!mainFeaturedMediaId) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const mainFeaturedMediaIndex = slides.findIndex((slide) =>
      slide.querySelector(`.product-media[data-media-id="${CSS.escape(mainFeaturedMediaId)}"]`)
    );

    if (mainFeaturedMediaIndex > 0) {
      this.slideshow?.select(mainFeaturedMediaIndex, undefined, { animate: false });
    }
  }

  /** Keeps the main product media first on mobile without changing desktop initialization. */
  #selectMainFeaturedMediaOnMobileLoad() {
    if (this.dataset.variantSelected === 'true') return;
    if (!this.closest("main[data-template^='product']")) return;

    const mainFeaturedMediaId = this.dataset.mainFeaturedMediaId;
    if (!mainFeaturedMediaId) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const mainFeaturedMediaIndex = slides.findIndex((slide) =>
      slide.querySelector(`.product-media[data-media-id="${CSS.escape(mainFeaturedMediaId)}"]`)
    );

    if (mainFeaturedMediaIndex > 0) {
      this.slideshow?.select(mainFeaturedMediaIndex, undefined, { animate: false });
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
